import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { ArrowRightIcon, CloseIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Heading3,
	IconButton,
	IconWrapper,
	Pill,
	Select,
	Spinner,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import { useJobTitleName } from '../../job/useJobTitleName';
import { StyrBadge } from './StyrBadge';

/** Sentinel option value meaning "create a brand-new Treams job title from this Styr job". */
const CREATE_NEW = '__create_new__';

const TableWrap = styled(Box)`
	border: 1px solid ${tokens.border.subtle};
	border-radius: 6px;
	overflow: hidden;
`;

const Row = styled(Flex)`
	align-items: center;
	gap: 16px;
	padding: 10px 16px;
	border-top: 1px solid ${tokens.border.subtle};

	&:first-child {
		border-top: none;
	}
`;

const HeaderRow = styled(Row)`
	background: ${tokens.surface.primary};
	font-size: 12px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: ${tokens.text.secondary};
`;

const NameCell = styled(Box)`
	flex: 2 1 0;
	min-width: 0;
	display: flex;
	gap: 4px;
	align-items: center;
`;

const LevelCell = styled(Box)`
	flex: 0 0 50px;
	color: ${tokens.text.secondary};
	font-size: 13px;
`;

const ArrowCell = styled(Box)`
	flex: 0 0 50px;
`;

const ActionCell = styled(Box)`
	flex: 3 1 0;
	min-width: 0;
`;

const JobName = styled.span`
	font-size: 14px;
	font-weight: 500;
	color: ${tokens.text.secondary};
`;

const Family = styled.div`
	font-size: 12px;
	color: ${tokens.text.secondary};
	opacity: 0.7;
`;

const LinkedText = styled.span`
	font-size: 13px;
	color: ${tokens.text.secondary};
`;

const LinkedList = styled(Flex)`
	flex-direction: column;
	gap: 8px;
`;

const LinkedRow = styled(Flex)`
	align-items: center;
	gap: 8px;
	min-width: 0;
`;

const EmptyState = styled(Box)`
	border: 1px dashed ${tokens.border.subtle};
	border-radius: 6px;
	padding: 24px;
	text-align: center;
	font-size: 13px;
	color: ${tokens.text.secondary};
	opacity: 0.8;
`;

/**
 * Interactive Styr -> Treams job-title mapping table. Lists every Styr job from the last sync
 * snapshot and shows which Treams JobTitles each is linked to. Support can attach several Treams
 * titles to one Styr job (or create a new one); applying writes the links and enqueues a sync so
 * the linked titles get enriched from Styr.
 */
export default function StyrJobMappingTable() {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const jobTitleName = useJobTitleName();
	const { addToast } = useToasts();

	const mappingsQuery = graphql.useStyrJobMappingsQuery({
		fetchPolicy: 'cache-and-network',
	});

	const jobTitlesQuery = graphql.useJobTitlesQuery({
		variables: { isArchived: false, first: 1000 },
		fetchPolicy: 'cache-and-network',
	});

	const [apply, { loading: applying }] = graphql.useApplyStyrJobMappingsMutation();
	const [syncStyr, { loading: syncing }] = graphql.useSyncStyrMutation();

	// styrJobID -> pending jobTitleIDs or CREATE_NEW (not yet applied)
	const [choices, setChoices] = useState<Record<string, string[]>>({});

	const mappings = useMemo(
		() => mappingsQuery.data?.styrJobMappings ?? [],
		[mappingsQuery.data?.styrJobMappings],
	);

	const jobTitleNodes = useMemo(
		() => (jobTitlesQuery.data?.jobTitles.edges ?? []).map((edge) => edge.node),
		[jobTitlesQuery.data?.jobTitles.edges],
	);

	const jobTitleNameByID = useMemo(() => {
		const names = new Map<string, string>();
		for (const node of jobTitleNodes) {
			names.set(node.id, localize(node.title).text);
		}
		return names;
	}, [jobTitleNodes, localize]);

	// Surface jobs that still need mapping: unsynced (no linked Treams JobTitle) on top, already
	// synced jobs at the bottom. Stable sort preserves the snapshot order within each group.
	const sortedMappings = useMemo(
		() =>
			[...mappings].sort(
				(a, b) => Number(a.linkedJobTitles.length > 0) - Number(b.linkedJobTitles.length > 0),
			),
		[mappings],
	);

	const pendingElsewhere = useMemo(() => {
		const taken = new Set<string>();
		for (const values of Object.values(choices)) {
			for (const value of values) {
				if (value !== CREATE_NEW) {
					taken.add(value);
				}
			}
		}
		return taken;
	}, [choices]);

	const optionsFor = (pending: string[]) => {
		const existing = jobTitleNodes
			.filter((node) => !node.styrData?.jobID && !pendingElsewhere.has(node.id))
			.map((node) => ({ value: node.id, title: localize(node.title).text }))
			.sort((a, b) => a.title.localeCompare(b.title));

		const options = [...existing];
		if (!pending.includes(CREATE_NEW)) {
			options.unshift({
				value: CREATE_NEW,
				title: t('settings.connectors.styr.mapping.createNew'),
			});
		}

		return options;
	};

	const pendingCount = Object.values(choices).reduce((sum, values) => sum + values.length, 0);

	const handleAdd = (styrJobID: string, value?: string | number) => {
		if (typeof value !== 'string' || !value) {
			return;
		}

		setChoices((prev) => ({
			...prev,
			[styrJobID]: [...(prev[styrJobID] ?? []), value],
		}));
	};

	const handleRemovePending = (styrJobID: string, value: string) => {
		setChoices((prev) => {
			const nextValues = (prev[styrJobID] ?? []).filter((item) => item !== value);
			const next = { ...prev, [styrJobID]: nextValues };
			if (nextValues.length === 0) {
				delete next[styrJobID];
			}
			return next;
		});
	};

	const handleApply = async () => {
		const inputMappings = Object.entries(choices).flatMap(([styrJobID, values]) =>
			values.map((value) =>
				value === CREATE_NEW
					? {
							styrJobID,
							action: graphql.StyrJobMappingAction.CreateNew,
						}
					: {
							styrJobID,
							action: graphql.StyrJobMappingAction.LinkExisting,
							jobTitleID: value,
						},
			),
		);

		try {
			await apply({ variables: { input: { mappings: inputMappings } } });

			// Linking only writes styrData.jobID; trigger a sync so the linked titles get enriched.
			await syncStyr();

			addToast(t('settings.connectors.styr.mapping.applied'), {
				appearance: 'success',
				autoDismiss: true,
			});

			setChoices({});
			await Promise.all([mappingsQuery.refetch(), jobTitlesQuery.refetch()]);
		} catch {
			addToast(t('settings.connectors.styr.mapping.applyError'), {
				appearance: 'error',
				autoDismiss: true,
			});
		}
	};

	const loading = mappingsQuery.loading && !mappingsQuery.data;

	return (
		<Box>
			<Flex alignItems="center" mb={2} style={{ gap: 8 }}>
				<Heading3>{t('settings.connectors.styr.mapping.title')}</Heading3>
			</Flex>

			{loading ? (
				<Flex justifyContent="center" p={4}>
					<Spinner />
				</Flex>
			) : mappings.length === 0 ? (
				<EmptyState>{t('settings.connectors.styr.mapping.empty')}</EmptyState>
			) : (
				<>
					<TableWrap>
						<HeaderRow>
							<NameCell>
								{t('settings.connectors.styr.mapping.styrJob')}
								<StyrBadge />
							</NameCell>
							<LevelCell>{t('settings.connectors.styr.mapping.level')}</LevelCell>
							<ArrowCell />
							<ActionCell>{t('settings.connectors.styr.mapping.treamsJob')}</ActionCell>
						</HeaderRow>

						{sortedMappings.map((mapping) => {
							const pending = choices[mapping.styrJobID] ?? [];
							const linked = mapping.linkedJobTitles;

							return (
								<Row key={mapping.styrJobID}>
									<NameCell>
										<Box>
											<JobName>{mapping.styrJobName}</JobName>
											{(mapping.jobFamilyName || mapping.styrGroup) && (
												<Family>
													{[
														mapping.jobFamilyName,
														mapping.styrGroup && localize(mapping.styrGroup).text,
													]
														.filter(Boolean)
														.join(' · ')}
												</Family>
											)}
										</Box>
									</NameCell>
									<LevelCell>{mapping.styrLevel ?? '—'}</LevelCell>
									<ArrowCell>
										<IconWrapper size={16}>
											<ArrowRightIcon />
										</IconWrapper>
									</ArrowCell>
									<ActionCell>
										<LinkedList>
											{linked.map((jobTitle) => (
												<LinkedRow key={jobTitle.id}>
													<LinkedText>{jobTitleName(jobTitle)}</LinkedText>
													<Pill small tint="secondary">
														{t('settings.connectors.styr.mapping.synced')}
													</Pill>
												</LinkedRow>
											))}
											{pending.map((value) => (
												<LinkedRow key={value}>
													<LinkedText>
														{value === CREATE_NEW
															? t('settings.connectors.styr.mapping.createNew')
															: (jobTitleNameByID.get(value) ?? value)}
													</LinkedText>
													<IconButton
														small
														type="plain"
														icon={<CloseIcon />}
														aria-label={t('settings.connectors.styr.mapping.remove')}
														onClick={() => handleRemovePending(mapping.styrJobID, value)}
													/>
												</LinkedRow>
											))}
											<Select
												small
												search
												placeholder={
													linked.length > 0 || pending.length > 0
														? t('settings.connectors.styr.mapping.addAnother')
														: t('settings.connectors.styr.mapping.choose')
												}
												options={optionsFor(pending)}
												onChange={(value) => handleAdd(mapping.styrJobID, value)}
											/>
										</LinkedList>
									</ActionCell>
								</Row>
							);
						})}
					</TableWrap>

					<Flex mt={3} justifyContent="flex-end">
						<Button
							type="primary"
							title={t('settings.connectors.styr.mapping.apply')}
							loading={applying || syncing}
							disabled={pendingCount === 0 || applying || syncing}
							onClick={handleApply}
						/>
					</Flex>
				</>
			)}
		</Box>
	);
}
